// The learner API's shapes (ADR-037; contracts 1.7 section 7): the catalogue, the knowledge check as a
// learner sees it, an attempt and its result, a progress event, and each title's completion state.
//
// Every shape is strict, like the rest of the contracts: a body that names a user, a tenant or a title,
// or any other extra field, is refused (ADR-037 section 3), and so is a response key this version does
// not know. The catalogue grows only by new optional keys in a minor version; the api and the web ship
// from one repository against one contracts version, so they move together. No shape here carries an
// object key or a store URL; media is reached by title id through the api's own routes.
import { z } from "zod";
import { Citation } from "./citation.js";
import { KnowledgeCheckAttempt, OptionId } from "./knowledge-check.js";
import { Id, NonEmpty, strictObject } from "./primitives.js";
import { Completion, LearningPath, TrainingTitle } from "./training.js";
// ── The catalogue ─────────────────────────────────────────────────────────
const { media: _media, ...titleShape } = TrainingTitle.shape;
/**
 * A title as the catalogue serves it: the served `TrainingTitle` (a published tenant version or the
 * pack's edition, with ADR-036's intro-card fields) without its `media` block, which holds an object
 * key, and with `playable`: whether the title has a video now. A playable title carries the measured
 * `runtimeSeconds`; one that is not carries `plannedRuntimeSeconds` (D-75), and the client shows it as
 * coming.
 */
export const CatalogueTitle = strictObject({ ...titleShape, playable: z.boolean() }).superRefine((t, ctx) => {
    const bad = (path, message) => {
        ctx.addIssue({ code: "custom", message: `title ${t.id}: ${message}`, path: [path] });
    };
    if (t.playable) {
        if (t.runtimeSeconds === undefined)
            bad("runtimeSeconds", "a playable title carries runtimeSeconds, measured from its video (D-75)");
        if (t.plannedRuntimeSeconds !== undefined)
            bad("plannedRuntimeSeconds", "plannedRuntimeSeconds is for a title with no video (D-75)");
    }
    else {
        if (t.runtimeSeconds !== undefined)
            bad("runtimeSeconds", "runtimeSeconds is measured from a video; a title with none carries plannedRuntimeSeconds (D-75)");
        if (t.plannedRuntimeSeconds === undefined)
            bad("plannedRuntimeSeconds", "a title with no video carries plannedRuntimeSeconds (D-75)");
    }
});
/** A section of the catalogue: its label is the titles' `section`, its id that label slugged. */
export const CatalogueSection = strictObject({
    id: Id,
    label: NonEmpty.max(120),
});
/** The slug a section's id is: lower case, each run of anything but letters and digits one dash. */
export function sectionId(label) {
    return label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
}
/**
 * GET /v1/learning/catalogue. Titles in the pack's teaching order (never sorted); sections in the order
 * of their first title; paths keep only visible titles, and a path left empty is dropped.
 */
export const LearnerCatalogue = strictObject({
    sections: z.array(CatalogueSection),
    titles: z.array(CatalogueTitle),
    paths: z.array(LearningPath),
}).superRefine((c, ctx) => {
    const ids = new Set(c.titles.map((t) => t.id));
    if (ids.size !== c.titles.length)
        ctx.addIssue({ code: "custom", message: "a title appears once", path: ["titles"] });
    const sections = new Set(c.sections.map((s) => s.id));
    if (sections.size !== c.sections.length)
        ctx.addIssue({ code: "custom", message: "a section appears once", path: ["sections"] });
    c.titles.forEach((t, i) => {
        if (t.section !== undefined && !sections.has(sectionId(t.section)))
            ctx.addIssue({
                code: "custom",
                message: `title ${t.id}: its section "${t.section}" is not among the sections`,
                path: ["titles", i, "section"],
            });
    });
    c.paths.forEach((p, i) => {
        p.titleIds.forEach((id, j) => {
            if (!ids.has(id))
                ctx.addIssue({
                    code: "custom",
                    message: `path ${p.id}: title ${id} is not among the visible titles`,
                    path: ["paths", i, "titleIds", j],
                });
        });
    });
});
// ── The knowledge check, as a learner sees it ─────────────────────────────
/** A question without its answer, why, citation or beat (ADR-023 section 3). */
export const LearnerQuestion = strictObject({
    id: Id,
    prompt: NonEmpty,
    options: z
        .array(strictObject({ id: OptionId, text: NonEmpty }))
        .min(2)
        .max(4),
});
/** GET /v1/learning/titles/:titleId/check: the served version's question set, answers removed. */
export const LearnerCheck = strictObject({
    titleId: Id,
    contentVersion: NonEmpty,
    passMarkPct: z.number().min(0).max(100), // the mark an attempt is scored against now (default 80)
    segments: z
        .array(strictObject({
        n: z.number().int().positive(),
        label: NonEmpty,
        questions: z.array(LearnerQuestion).min(1),
    }))
        .min(1),
});
/** POST /v1/learning/titles/:titleId/check/attempts. Every question of the served set, once. */
export const AttemptBody = strictObject({
    answers: z.array(strictObject({ questionId: Id, optionId: OptionId })).min(1),
}).superRefine((b, ctx) => {
    const seen = new Set();
    b.answers.forEach((a, i) => {
        if (seen.has(a.questionId))
            ctx.addIssue({
                code: "custom",
                message: `question ${a.questionId} is answered once`,
                path: ["answers", i, "questionId"],
            });
        seen.add(a.questionId);
    });
});
// ── Progress ──────────────────────────────────────────────────────────────
export const ProgressEvent = z.enum(["start", "progress", "end"]);
/**
 * POST /v1/learning/titles/:titleId/progress. Names neither a user, a tenant nor a title: those are the
 * session's and the path's. The server clamps both numbers and caps the rise by wall-clock time
 * (ADR-037 section 3); the schema only refuses values that are not numbers in range.
 */
export const ProgressBody = strictObject({
    event: ProgressEvent,
    pct: z.number().min(0).max(100),
    positionSeconds: z.number().nonnegative(),
});
// ── Completion state ──────────────────────────────────────────────────────
export const TITLE_STATES = [
    "not-started",
    "in-progress",
    "completed",
    "attestation-needed",
    "attestation-out-of-date",
];
export const TitleState = z.enum(TITLE_STATES);
/** One title's state for the caller, worked out on the server (ADR-037 section 6). */
export const LearnerCompletion = strictObject({
    titleId: Id,
    state: TitleState,
    completion: Completion.optional(), // against the served contentVersion
    resumeAtSeconds: z.number().nonnegative().optional(), // absent when not started or finished
    checkAvailable: z.boolean(), // the title has a question set and is watched to its completeAtPct
});
/** GET /v1/learning/completions: one item per visible title, in the catalogue's order. */
export const LearnerCompletions = strictObject({ items: z.array(LearnerCompletion) });
/**
 * GET /v1/learning/completions when the tenant's analytics policy turns individual records off: a typed
 * answer, told apart from "nothing yet" (an empty list) and from a failure (E14-S3-T4).
 */
export const IndividualRecordsOff = strictObject({ individualRecords: z.literal("off") });
// ── An attempt's result ───────────────────────────────────────────────────
const { userId: _userId, ...attemptShape } = KnowledgeCheckAttempt.shape;
/**
 * The attempt as its maker sees it: the stored row without the user id, which is theirs. Keeps every
 * rule of `KnowledgeCheckAttempt`: each question once, each a question of the title, and passed
 * agreeing with the score and the mark.
 */
export const LearnerAttempt = strictObject(attemptShape).superRefine((a, ctx) => {
    const seen = new Set();
    a.answers.forEach((x, i) => {
        if (seen.has(x.questionId))
            ctx.addIssue({
                code: "custom",
                message: `attempt ${a.id}: question ${x.questionId} is answered once`,
                path: ["answers", i, "questionId"],
            });
        seen.add(x.questionId);
        if (!x.questionId.startsWith(`${a.titleId}.`))
            ctx.addIssue({
                code: "custom",
                message: `attempt ${a.id}: question ${x.questionId} is not a question of title ${a.titleId}`,
                path: ["answers", i, "questionId"],
            });
    });
    if (a.passed !== a.scorePct >= a.passMarkPct)
        ctx.addIssue({
            code: "custom",
            message: `attempt ${a.id}: passed is true exactly when scorePct is at or above passMarkPct`,
            path: ["passed"],
        });
});
/** 201 from an attempt: the attempt, the questions answered wrongly with the right answer, and the state. */
export const AttemptResult = strictObject({
    attempt: LearnerAttempt,
    missed: z.array(strictObject({ questionId: Id, answer: OptionId, why: NonEmpty, citation: Citation })),
    completion: LearnerCompletion,
});
//# sourceMappingURL=learner.js.map